import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";
import { getProducts, type BrokerProduct } from "@/lib/brokerProducts";
import { ExchangeHeroGraphic } from "@/components/ExchangeHeroGraphic";

// The API Exchange — the post-login landing (BRK-S021). Leads with the
// submission CTA and shows the product marketplace, SERVER-DRIVEN from the PMS
// product catalog (GET /api/broker/products — BRK-S051/PMS-S141). The shell
// chrome (nav, identity, sign-out) is the surrounding PortalShell (BRK-S035);
// this page is just the Exchange content on the light Exchange canvas.
//
// The "Direct Markets" carrier row is presentational-only scaffolding (dummy
// cards) for eventual direct-appointment integrations — it links to nothing yet.

// Small crimson four-chevron mark used to badge the section headers.
function SignalTick() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="-32 -32 64 64"
      fill="none"
      aria-hidden="true"
      className="shrink-0 text-brk-accent"
    >
      <g
        stroke="currentColor"
        strokeWidth="9"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M16.8 3 L26.8 -26.8 L-3 -17" />
        <path d="M-16.8 3 L-26.8 -26.8 L3 -17" />
        <path d="M16.8 -3 L26.8 26.8 L-3 17" />
        <path d="M-16.8 -3 L-26.8 26.8 L3 17" />
      </g>
    </svg>
  );
}

function SectionHeading({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <h2 className="flex items-center gap-2.5 text-lg font-semibold text-brk-text">
        <SignalTick />
        {children}
      </h2>
      {/* Presentational-only (dummy). */}
      <button
        type="button"
        className="flex items-center gap-1 text-sm font-medium text-brk-accent transition-colors hover:text-brk-accent-strong"
      >
        View all <span aria-hidden="true">›</span>
      </button>
    </div>
  );
}

export function PortalPage() {
  const { authedRequest } = useAuth();
  const [products, setProducts] = useState<BrokerProduct[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { items } = await getProducts(authedRequest);
        if (!cancelled) setProducts(items);
      } catch {
        // A failed catalog read degrades just the grid — the CTA stays (NN #7).
        if (!cancelled)
          setError("Couldn’t load the marketplace — reload to try again.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authedRequest]);

  return (
    <div className="min-h-full bg-brk-bg px-4 py-8 text-brk-text sm:px-8 sm:py-10">
      <div className="flex flex-col gap-10">
        {/* Hero */}
        <section className="relative">
          {/* Decorative illustration (lg+; the hero copy stays constrained left). */}
          <ExchangeHeroGraphic className="pointer-events-none absolute -top-2 right-0 hidden w-[30rem] max-w-[46%] lg:block" />
          <div className="relative z-10 flex max-w-2xl flex-col gap-4">
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-brk-accent">
              API Exchange
            </p>
            <h1 className="text-3xl font-bold tracking-tight text-brk-text sm:text-5xl">
              Get your risk to market in minutes
            </h1>
            <p className="max-w-prose text-brk-dim">
              Start a submission and we’ll route you to the right markets.
            </p>
            <div className="pt-1">
              <Link
                to="/submissions/new"
                className="inline-flex items-center gap-2 rounded-lg bg-brk-accent px-6 py-3 font-medium text-white transition-colors hover:bg-brk-accent-strong"
              >
                <span aria-hidden="true" className="text-lg leading-none">
                  +
                </span>
                Start submission
              </Link>
            </div>
          </div>
        </section>

        <hr className="border-brk-border" />

        {/* Marketplace — server-driven product catalog. */}
        <section className="flex flex-col gap-4">
          <SectionHeading>Exchange Products</SectionHeading>
          {error && <p className="text-sm text-brk-dim">{error}</p>}
          {!error && products === null && (
            <p role="status" className="text-sm text-brk-dim">
              Loading the marketplace…
            </p>
          )}
          {products && products.length === 0 && (
            <p className="text-sm text-brk-dim">
              No products are live right now — check back soon.
            </p>
          )}
          {products && products.length > 0 && (
            <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {products.map((p) => (
                <li
                  key={p.id}
                  className="group flex flex-col gap-6 rounded-2xl border border-brk-border bg-brk-panel p-5 transition-shadow hover:shadow-md"
                >
                  <div className="flex items-start justify-between gap-2">
                    <span
                      aria-hidden="true"
                      className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-brk-accent/10 text-brk-accent"
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M4 9h16M4 9l1.5-4.5A1 1 0 0 1 6.45 4h11.1a1 1 0 0 1 .95.68L20 9M4 9v9a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V9"
                          stroke="currentColor"
                          strokeWidth="1.6"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700">
                      <span aria-hidden="true">●</span> Active
                    </span>
                  </div>
                  <div className="flex items-end justify-between gap-2">
                    <div className="flex flex-col gap-0.5">
                      <span className="font-semibold text-brk-text">
                        {p.name}
                      </span>
                      <span className="text-sm text-brk-dim">
                        {p.distributionMode === "marketplace"
                          ? "Multiple markets, one submission"
                          : "Signal-written coverage"}
                      </span>
                    </div>
                    <span
                      aria-hidden="true"
                      className="text-brk-dim transition-colors group-hover:text-brk-accent"
                    >
                      →
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
